define('util/dcChartUtils',[
    'lib/knockout',
    'lib/lodash4',
    'moment',
    'lib/d3',
    'lib/dc',
    'util/d3TooltipUtils',
    'extensions/i18n/knockout.i18n'
], function(
    ko,
    _,
    moment,
    d3,
    dc,
    d3tip
) {
    'use strict';

    var GRANULARITY = {
        WEEK: "weeks",
        DAY: "days",
        INTERVAL: "minutes"
    };

    var COLORS = [
        '#1f77b4', '#ff7f0e', '#2ca02c',
        '#9467bd', '#8c564b', '#e377c2',
        '#7f7f7f', '#bcbd22', '#17becf'
    ];

    // ===================================================
    // Charting Helper Functions
    // ===================================================

    /**
     * (Private) Provides a list of children when given a component chart.
     *
     * @param   chart           (object) The parent chart.
     *
     * @return                  (array) An array of chart objects.
     **/
    var getChartChildren = function(chart) {

        return chart.children();

    };

    /**
     * (Private) Pushes chart data into an array.
     *
     * @param   list            (array) The array to push data into.
     * @param   charts          (array) An collection of charts to extract data from.
     *
     * @return                  (array) The list array with data pushed into it.
     **/
    var appendChartData = function(list, charts) {

        _.each(charts, function(chart) {
            list.push(chart.data()[0]);
        });

        return list;

    };

    /**
     * (Public) Utility function to calculate the granularity a view should
     * use when given a start and end moment.
     *
     * @param   startMoment     (moment) The beginning moment used for calculation.
     * @param   endMomenet      (moment) The ending moment used for calculation.
     *
     * @return                  (string) The granularity string.
     **/
    var getGranularity = function (startMoment, endMoment) {

        var timeRange = endMoment.diff(startMoment, 'days');

        if (timeRange > 31) {
            return GRANULARITY.WEEK;
        } else if (timeRange > 1) {
            return GRANULARITY.DAY;
        }

        return GRANULARITY.INTERVAL;

    };

    /**
     * (Public) Determines if two chart references are references to the
     * same chart by comparing their IDs.
     *
     * @param   compositeChart  (object) The chart initial chart used from comparison.
     * @param   chart           (object) The chart to compare compositeChart against.
     *
     * @return                  (boolean) True if chart IDs are the same, otherwise false.
     **/
    var isCurrentCompositeChart = function(compositeChart, chart) {

        return compositeChart.chartID() === chart.chartID();

    };

    /**
     * (Public) Formats a date given a date and a granularity.
     *
     * @param   date                (string || moment) The date to be formatted.
     * @param   chartGranularity    (string) The granularity used to specify the format of
     *                              the date. Granularity can be calculated by
     *                              using getGranularity().
     *
     * @return                      (string) The formatted date string.
     **/
    var formatChartDate = function(date, chartGranularity) {

        if(chartGranularity === GRANULARITY.INTERVAL){
            return  ko.i18n.formatDate(date, "LT");
        }

        return ko.i18n.formatDate(date, "L");

    };

    /**
     * (Public) Provides a color scale that includes a color for the target series.
     *
     * @param   color               (string) Hex color for target series
     * @param   length              (int) The number of series needing colors
     *
     * @return                      (object) Object for d3 ordinal scale containing colors.
     **/
    var modifyColorScaleForTarget = function (color, length) {

        var newColors = _.clone(COLORS);
        newColors[length- 1] = color;
        return d3.scale.ordinal().range(newColors);

    };

    /**
     * (Public) Checks if all data points are at least 1.
     *
     * @param   groups              (Array) An array of all of the groups of data for the chart.
     *
     * @return                      (boolean) Returns true if all data points are at least 1
     **/
    var checkIsInMinutes = function (groups) {
            // if all values are < 1, we will convert to seconds.
            // if any value is >= 1, we will display in minutes
            return _.some(groups, function (group) {
                return _.some(group.all(), function(g) {
                    return g.value >= 1;
                });
            });
    };

    /**
     * (Public) Finds the nearest point, or set of points, to a given value in an array.
     *
     * @param pointArray            (Array) An array of all the points on an axis
     * @param val                   (string || int) The value we want to get closest to
     * @param bounded               (boolean) If true, provides the closest low and high points. If false,
     *                              provides the closest point
     *
     * @return                      (Array || string || int) Returns either an array of closest low and high points,
     *                              or the value of the closest point
     */
    var findNearestPoint = function ( pointArray, val, bounded ) {

        var low = -1, hi = pointArray.length - 1;

        while (hi - low > 1) {
            var mid = Math.round((low + hi) / 2);
            if (pointArray[mid] <= val) {
                low = mid;
            } else {
                hi = mid;
            }
        }

        if (pointArray[low] === val) {
            hi = low;
        }

        if (bounded) {
            return [pointArray[low], pointArray[hi]];
        } else {
            if (Math.abs(pointArray[low] - val) < Math.abs(pointArray[hi] - val)) {
                return pointArray[low];
            } else {
                return pointArray[hi];
            }
        }

    };

    /**
     * (Public) Finds the dots across all series that lie on the desired X coordinate
     *
     * @param dots                  (Array) An array of all the dots across all series
     * @param searchVal             (Array) An array of the x/y coordinate we're looking for [x, y]
     *
     * @return                      (Array) Returns an array of dot indices in order of their proximity
     *                                  to the desired x/y coordinate
     */
    var findNearestDots = function (dots, searchVal) {

        if (! (dots instanceof Array)) {
            return null;
        }

        // Set up the accessor/compare functions for manipulating the dot objects
        var _xAccessor = function(d) {
            if (_.has(d, 'x')) {
                return d.x;
            } else {
                return undefined;
            }
        };
        var _yAccessor = function(d) {
            if (_.has(d, 'y')) {
                return d.y;
            } else {
                return undefined;
            }
        };
        var _compareX = function(d, v) {
            return _xAccessor(d) - v[0];
        };

        // Map the x/y coordinates of each dot to an array
        var dotMap = _.map(dots, function(dot) {
            var dotElement = d3.select(dot);
            return {
                x: dotElement.attr('cx'),
                y: dotElement.attr('cy')
            };
        });

        // Use d3 bisects to find the nearest x-axis coordinate to the search value
        var bisectX = d3.bisector(_compareX).left;
        var xIndex = d3.max([0, bisectX(dotMap, searchVal) - 1]);
        var currentXValue = _xAccessor(dotMap[xIndex]);

        if (typeof currentXValue === 'undefined') {
            currentXValue = xIndex;
        }

        var nextXIndex = xIndex + 1;
        var nextXValue = _xAccessor(dotMap[nextXIndex]);

        if (typeof nextXValue === 'undefined') {
            nextXValue = nextXIndex;
        }

        // Determine whether the search value is closer to the x-coordinate before or after
        var closestXIndex = xIndex;
        if (Math.abs(nextXValue - searchVal[0]) < Math.abs(currentXValue - searchVal[0])) {
            closestXIndex = nextXIndex;
            currentXValue = nextXValue;
        }

        // Get an array of all the dots that exist on the closest x-coordinate
        var dotsByX = _.filter(dotMap, function(dot) {
            return _xAccessor(dot) === currentXValue;
        });

        var closestDots = [];
        if (dotsByX.length === 1) {
            closestDots.push(xIndex);
        } else {
            // Sort the array by the dot's proximity to the search value based on its y-axis value
            var sortedDots = _.sortBy(dotsByX, [function(dot) {
                return Math.abs(_yAccessor(dot) - searchVal[1]);
            }]);

            // Get the indices of the sorted dots
            _.forEach(sortedDots, function(sortedDot) {
                var dotIndex = _.findIndex(dotMap, function(dot) {
                    return _xAccessor(dot) === _xAccessor(sortedDot) && _yAccessor(dot) === _yAccessor(sortedDot);
                });

                if (dotIndex >= 0) {
                    closestDots.push(dotIndex);
                }
            });
        }

        return closestDots;

    };

    /**
     * (Public) Determine if the mouse cursor is within the bounds of the chart itself
     *
     * @param chart                 (Object) A composite DC chart object.
     * @param mouseXY               (Array) An array of the x/y coordinates of the mouse cursor [x, y]
     *
     * @return                      (Boolean) Returns whether the cursor is within the bounds
     */
    var isMouseInChartBounds = function(chart, mouseXY) {
        var inLeftBound = mouseXY[0] > chart.margins().left;
        var inRightBound = mouseXY[0] < (chart.width() - chart.margins().right);
        var inTopBound = mouseXY[1] > chart.margins().top;
        var inBottomBound = mouseXY[1] < (chart.height() - chart.margins().bottom);

        return inLeftBound && inRightBound && inTopBound && inBottomBound;
    };

    // ===================================================
    // DOM Utilities
    // ===================================================

    /**
     * (Public) Store a plain object to an element using knockout's domData utility.
     *
     * @param   domDataKey          (string) A unique key for identify a data object on an element.
     * @param   element             (Object) The DOM element to store the data to.
     * @param   data                (Object) A plain data object with key:value pairs that contains
     *                              data to store.
     **/
    var setDomData = function (domDataKey, element, data) {

        ko.utils.domData.set(element, domDataKey, data);

    };

    /**
     * (Public) Lookup a data object (key:value pair) for an element.
     *
     * @param   domDataKey          (string) A unique key for identify a data object on an element.
     * @param   element             (Object) The DOM element that has stored the data.
     * @param   key                 (string) A string key for the data value to return.
     *
     * @return                      (Object) The data value stored for the key requsted.
     **/
    var getDomDataValue = function (domDataKey, element, key) {

        var domData = ko.utils.domData.get(element, domDataKey);
        if(domData){
            return domData[key];
        }
    };

    /**
     * (Public) Update a data value stored in DOM data on an element.
     *
     * @param   domDataKey          (string) A unique key for identify a data object on an element.
     * @param   element             (Object) The DOM element to store the data to.
     * @param   key                 (Object) A string key for the data value to overwrite.
     * @param   value               (String|Array|Object) A value to store for a key on an element.
     **/
    var updateDomDataValue = function (domDataKey, element, key, value) {

        var domData = ko.utils.domData.get(element, domDataKey);
        domData[key] = value;

        ko.utils.domData.set(element, domDataKey, domData);

    };

    // ===================================================
    // Tooltip Utilities
    // ===================================================

    /**
     * (Private) Returns true if the legend element that corresponds to a series is
     * in toggled state.
     *
     * @param   node            (SVG Node Object) The svg node representing a legend node from
     *                          within a DC chart.
     * @param   seriesLabel     (string) The label text for the corresponding series in
     *                          the chart.
     *
     * @return                  (boolean) True if the legend is toggled in an active state,
     *                          false otherwise.
     **/
    var isLegendForSeries = function(node, seriesLabel) {

        return d3.select(node).text() === seriesLabel;

    };

    /**
     * (Public) Creates a tooltip element from d3TooltipUtils.
     *
     * @return                  (object) The d3Tip object.
     **/
    var createTooltip = function() {

        var tip = d3tip()
            .attr('class','chart-tooltip')
            .offset([-10,0]);

        return tip;

    };

    /**
     * (Private) Builds the body of the tooltip (content)
     *
     * @param   d                   (object) The data object the corresponds to the hovered element.
     * @param   compositeChart      (object) A composite DC chart object.
     * @param   chartGranularity    (string) String generated by getGranularity().
     * @param   scaleType           (string) The type of y-axis (used to format data).
     * @param   valueFormat         (object) An array of functions to determine the correct string key for formatting the series value
     * @param   dateFormat          (object) A function to specify the formatting of the date object
     *
     * @return                      (string) The formatted body of the tooltip.
     **/
    var generateTooltipMarkup = function(d, compositeChart, chartGranularity, scaleType, valueFormat, dateFormat) {

        var keyToMatch = d.data.key;
        var charts = dc.chartRegistry.list();
        var output = [];

        var filterCharts = _.partial(isCurrentCompositeChart, compositeChart);

        var completeSeriesData = _(charts)
            .filter(filterCharts)
            .map(getChartChildren)
            .reduce(appendChartData,[])
            .valueOf();

        function isKeyMatch(datum) {
            return datum.data.key === keyToMatch;
        }

        function formatOutput(output) {

            var formattedOutput = "<h5>";
            if (!_.isUndefined(dateFormat)) {
                formattedOutput += ko.i18n.formatDate(keyToMatch, dateFormat(keyToMatch));
            } else {
                formattedOutput += formatChartDate(keyToMatch,chartGranularity);
            }

            formattedOutput += "</h5><dl>";

            _.each(output, function(line) {
                formattedOutput += line;
            });

            formattedOutput += "</dl>";

            return formattedOutput;

        }

        _.each(completeSeriesData, function(series) {

            var seriesLabel = series.name;
            var legendNodes = compositeChart.svg().selectAll(".legend")[0];

            // Generate output for each series if the series
            // has not been toggled to a hidden state. We determine this
            // by checking the the legend to see if it is in a opaque state.

            function generateOutput(datum) {

                _.each(legendNodes, function(node, index) {

                    if(isLegendForSeries(node, seriesLabel) && compositeChart.children()[index].chartIsActive()){

                        var formattedValue = "";

                        if(scaleType == 'percentage'){
                            formattedValue =  ko.i18n.format(datum.data.value, "p0");
                        } else if (scaleType == 'time') {
                            formattedValue = ko.i18n.formatDuration(datum.data.value*60000);
                        } else if (!_.isUndefined(valueFormat) && !_.isUndefined(valueFormat[index])) {
                            formattedValue = valueFormat[index](datum.data.value);
                        } else {
                            formattedValue = datum.data.value;
                        }

                        if(d.layer === datum.layer){
                            output += "<dt class='focused'>" + seriesLabel + ":</dt><dd>" + formattedValue + "</dd>";
                        } else {
                            output += "<dt>" + seriesLabel + ":</dt><dd>" + formattedValue + "</dd>";
                        }

                    }

                });

                return output;

            }

            var outputForSeries = _(series.values)
                .filter(isKeyMatch)
                .map(generateOutput)
                .valueOf();

            _.merge(output,outputForSeries);

        });


        return formatOutput(output);

    };

    /**
     * (Public) Toggles a basic tooltip in view. Currently used for columnChart.
     *
     * @param   tip                 (object) The d3tip object.
     * @param   chart               (object) The DC chart object that owns the tooltip.
     * @param   chartGranularity    (string) String generated by getGranularity().
     * @param   scaleType           (string) The type of y-axis (used to format data).
     * @param   d                   (object) The data object the corresponds to the hovered element.
     **/
    var showBasicTooltip = function(tip, chart, chartGranularity, scaleType, d) {

        tip.html(function(){
            return generateTooltipMarkup(d, chart, chartGranularity, scaleType);
        });
        tip.show(d);

    };

    /**
     * (Public) Toggles a basic tooltip out of view. Currently used for columnChart.
     *
     * @param   tip                 (object) The d3tip object.
     * @param   d                   (object) The data object the corresponds to the hovered element.
     **/
    var hideBasicTooltip = function(tip, d) {

        tip.hide(d);

    };

    /**
     * (Public) Toggles a tooltip in for the line chart. The key difference between the this function
     * and showBasicTooltip is that this function also makes the dots in the chart appear active.
     *
     * @param   tip                 (object) The d3tip object.
     * @param   chart               (object) The DC chart object that owns the tooltip.
     * @param   chartGranularity    (string) String generated by getGranularity().
     * @param   scaleType           (string) The type of y-axis (used to format data).
     * @param   valueFormat         (object) An array of functions to determine the correct string key for formatting the series value.
     * @param   dateFormat          (object) A function to specify the formatting of the date object
     * @param   showGuideline       (boolean) Determines whether to show an interactive line in addition to the tooltip.
     * @param   d                   (object) The data object the corresponds to the hovered element.
     **/
    var showLineChartTooltip = function(tip, chart, chartGranularity, scaleType, valueFormat, dateFormat, showGuideline, d) {

        var index;
        var activeElem = this;

        // toggle dots for line charts
        var dots = chart.svg().selectAll("g.aggregate-tooltip-container circle.dot")[0];

        var seriesCnt = _.isArray(chart.svg().selectAll("g.sub")[0]) ?
                            chart.svg().selectAll("g.sub")[0].length : 1;

        if (showGuideline) {
            var x, indices = [];
            var xyCoordinate = d3.mouse(this); // Get the current cursor position (Object) [x, y]

            // Hide the guideline if we're not over the chart itself
            if (!isMouseInChartBounds(chart, xyCoordinate)) {
                hideLineChartTooltip(tip, chart, true);
                return;
            }

            // Adjust mouse coordinates to get the corresponding location in the chart
            xyCoordinate[0] -= chart.margins().left;
            xyCoordinate[1] -= chart.margins().top;

            // Find the dots that lie on the nearest x-axis coordinate
            indices = findNearestDots(dots, xyCoordinate);
            index = indices[0]; // Get the closest dot to the cursor position

            if (_.isUndefined(dots[index])) {
                hideLineChartTooltip(tip, chart, true);
                return;
            }

            // Use the closest dot for the tooltip's data
            tip.html(function() {
                return generateTooltipMarkup(d3.select(dots[index]).data()[0], chart, chartGranularity, scaleType, valueFormat, dateFormat);
            });

            // hide previous dots
            hideLineChartTooltip(tip, chart, false, d3.select(dots[index]).data()[0]);

            // set dots to appear active
            _.forEach(indices, function (position) {
                d3.select(dots[position]).attr("style", "fill-opacity: 0.8; stroke-opacity: 0.8");

                // Get the x-axis coordinate for our guideline
                if (_.isUndefined(x)) {
                    x = d3.select(dots[position]).attr('cx');
                }
            });

            // Find the existing guideline and tooltip anchor
            var guideline = chart.svg().select("line.guideline")[0];
            var tooltipAnchor = chart.svg().select("circle.anchor")[0];

            // If the guideline exists, update its location with the new x coordinate, otherwise create it
            if (guideline.length < 1 || guideline[0] === null) {
                var lineHeight = chart.y() ? chart.y().range()[0] :
                                             chart.rightY() ? chart.rightY().range()[0] : 0;
                chart.svg().select("g.aggregate-tooltip-container").append("line")
                    .attr("class", "guideline")
                    .attr("x1", x)
                    .attr("y1", lineHeight) // Set the line height to match the y-axis scale
                    .attr("x2", x)
                    .attr("y2", 0)
                    .style("stroke-width", 1)
                    .style("stroke-opacity", 0.5)
                    .style("stroke", "red")
                    .style("fill", "none");
            } else {
                d3.select(guideline[0])
                    .attr("x1", x)
                    .attr("x2", x);
            }

            // If the anchor exists, update its location with the new x/y coordinates, otherwise create it
            if (tooltipAnchor.length < 1 || tooltipAnchor[0] === null) {
                chart.svg().select('g').append("circle")
                    .attr("class", "anchor")
                    .attr("cx", d3.mouse(this)[0])
                    .attr("cy", d3.mouse(this)[1])
                    .style("stroke-width", 1)
                    .style("stroke-opacity", 0)
                    .style("stroke", "red")
                    .style("fill", "none");

                tooltipAnchor = chart.svg().select("circle.anchor")[0];
            } else {
                d3.select(tooltipAnchor[0])
                    .attr("cx", d3.mouse(this)[0])
                    .attr("cy", d3.mouse(this)[1]);
            }

            // Tie the tooltip to the anchor element instead of the SVG container
            tip.direction('ne');
            tip.offset([0, 15]);
            tip.show(tooltipAnchor[0]);
            tip.style({'pointer-events': 'none'});
        } else {
            tip.html(function(){
                return generateTooltipMarkup(d, chart, chartGranularity, scaleType, valueFormat, dateFormat);
            });

            // Find the active dot
            index = _.findIndex(dots, function(dot) {
                return dot === activeElem;
            }) % (dots.length/seriesCnt);

            // set dots to appear active
            for(var i = 0; i < seriesCnt; i++){
                var position = i*(dots.length/seriesCnt) + index;
                d3.select(dots[position]).attr("style","fill-opacity: 0.8; stroke-opacity: 0.8");
            }

            tip.show(d);
        }
    };

    /**
     * (Public) Toggles a tooltip for the line chart out of view. Also cleans up .dot elements, toggling
     * them back into an inactive state.
     *
     * @param   tip                 (object) The d3tip object.
     * @param   chart               (object) The DC chart object that owns the tooltip.
     * @param   hideGuideline       (boolean) Determines whether to remove the guideline and tooltip anchor.
     * @param   d                   (object) The data object the corresponds to the hovered element.
     **/
    var hideLineChartTooltip = function(tip, chart, hideGuideline, d) {

        if (hideGuideline) {
            if (this === undefined || !isMouseInChartBounds(chart, d3.mouse(this))) {
                chart.svg().selectAll("line.guideline").remove();
                chart.svg().selectAll("circle.anchor").remove();
            } else {
                return;
            }
        }

        hideBasicTooltip(tip,d);
        if (_.flatten(chart.svg().select(".aggregate-tooltip-container").selectAll(".dot")).length > 1) {
            chart.svg().selectAll(".dot").attr("style","fill-opacity: 0.000001; stroke-opacity: 0.000001");
        }
    };

    return {

        // vars
        COLORS: COLORS,

        // utility
        getGranularity: getGranularity,
        isCurrentCompositeChart: isCurrentCompositeChart,
        formatChartDate: formatChartDate,
        modifyColorScaleForTarget: modifyColorScaleForTarget,
        checkIsInMinutes: checkIsInMinutes,
        findNearestPoint: findNearestPoint,

        // DOM utilities
        setDomData: setDomData,
        getDomDataValue: getDomDataValue,
        updateDomDataValue: updateDomDataValue,

        // tooltip
        createTooltip: createTooltip,
        showBasicTooltip: showBasicTooltip,
        hideBasicTooltip: hideBasicTooltip,
        showLineChartTooltip: showLineChartTooltip,
        hideLineChartTooltip: hideLineChartTooltip

    };

});

